import { useGetContext } from '@api/lendingAPI/lapiHooks';
import {
    getAccountsQueryConfig,
    getStateQueryConfig
} from '@api/lendingAPI/queryConfigs';
import { getAccountIsRBL } from '@helpers/Accounts/getAccountIsRBL';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import {
    fetchGetApplication,
    fetchGetApplicationsDocumentReviewFeedback,
    fetchGetDocumentUpload,
    fetchGetOnlineAccount
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import {
    groupByDocumentType,
    deserialiseLinkedOpenBankingAccounts,
    TDocuments
} from './TradingDataHelper';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { getDocumentTypes } from '../modals/uploadDocumentsModal/UploadDocumentsModalHelper';
import { REVENUE_BASED_LOAN_PRODUCT_TYPE } from '../multiLoanDashboard/accountsSelector';

export const useGetTradingHistoryData = () => {
    const stateKey = useStateKey();
    const isDE = getCountry() === 'DE';

    const { data: customerStateResponse } = useQuery({
        ...getStateQueryConfig(stateKey),
        suspense: true
    });

    const { data: context } = useGetContext();

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        suspense: true
    });

    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        suspense: true
    });

    const { data: onlineAccountsResponse } = useQuery({
        queryKey: ['online_accounts', stateKey],
        queryFn: () => fetchGetOnlineAccount({ stateKey: stateKey }),
        suspense: true
    });

    const { data: documentsResponse } = useQuery({
        queryKey: ['documents', stateKey, isDE],
        queryFn: () =>
            fetchGetDocumentUpload({
                stateKey,
                ...(isDE ? { query: { append_feedback: true } } : {})
            }),
        suspense: true
    });

    const openBankingBanks = context?.open_banking?.banks ?? [];
    const trueLayerBanks = context?.open_banking?.truelayer_banks ?? [];
    const finApiBanks = context?.open_banking?.finapi_banks ?? [];

    const allOpenBankingBanks = [
        ...openBankingBanks,
        ...trueLayerBanks,
        ...finApiBanks
    ];

    if (
        !customerStateResponse ||
        !applicationResponse ||
        !accountsResponse ||
        !onlineAccountsResponse ||
        !documentsResponse
    ) {
        throw new Error('Data fetching error');
    }

    const applicationId = applicationResponse.data?.application_id;
    const isDocumentReviewEnabled = isDE && Boolean(applicationId);

    const { data: documentReviewResponse, isError: documentReviewError } =
        useQuery({
            queryKey: ['document_review_feedback', stateKey, applicationId],
            queryFn: () =>
                fetchGetApplicationsDocumentReviewFeedback({
                    applicationId: applicationId!
                }),
            suspense: true,
            enabled: isDocumentReviewEnabled
        });

    if (
        isDocumentReviewEnabled &&
        (!documentReviewResponse || documentReviewError)
    ) {
        throw new Error('Data fetching error');
    }

    const { additional_info_requests = [], doc_requirement_reviews = [] } =
        documentReviewResponse?.data || {};

    const hasDocumentFeedback =
        (additional_info_requests && additional_info_requests.length > 0) ||
        (doc_requirement_reviews && doc_requirement_reviews.length > 0) ||
        documentsResponse.data.some(
            (doc: any) =>
                doc.attributes &&
                doc.attributes.document_feedback &&
                doc.attributes.document_feedback.length > 0
        );

    const rblAccount = accountsResponse.data.accounts.find((account) =>
        getAccountIsRBL(account)
    );

    const isRBLApplication =
        applicationResponse.data?.requested_products?.[0].product_type ===
        REVENUE_BASED_LOAN_PRODUCT_TYPE;

    return {
        documentTypes: getDocumentTypes(customerStateResponse),
        uploadedDocuments: groupByDocumentType(
            documentsResponse.data as unknown as TDocuments
        ),
        linkedAccounts: deserialiseLinkedOpenBankingAccounts(
            onlineAccountsResponse.data,
            allOpenBankingBanks
        ),
        hasRBL: !!rblAccount || isRBLApplication,
        documentFeedback: documentReviewResponse?.data,
        hasDocumentFeedback,
        applicationId
    };
};
